export interface PlaceCategory {
  id: string; // Google type string, e.g. "restaurant", "tourist_attraction"
  name: string; // Human-readable display name
}

// Photos carry provider-specific tokens resolved to a URL at render time via
// `photoUrl()` in `src/api/places.ts`. The server normalizes all providers down
// to Foursquare-shaped photos.
export interface FoursquarePlacePhoto {
  provider: 'foursquare';
  // Foursquare photo URLs are built as `${prefix}${size}${suffix}`, where
  // `size` is e.g. "original" or "400x300".
  prefix: string;
  suffix: string;
  widthPx: number;
  heightPx: number;
}

// Openly-licensed fallback photos (Wikimedia Commons, Mapillary) for places the
// commercial providers don't cover. These carry a ready URL instead of
// Foursquare's prefix/suffix tokens — see `photoUrl` in `src/api/places.ts`.
export interface UrlPlacePhoto {
  provider: 'wikimedia' | 'mapillary';
  url: string;
  widthPx: number;
  heightPx: number;
  /** Human-readable source, shown for attribution (CC-licensed imagery). */
  attribution?: string;
}

export type PlacePhoto = FoursquarePlacePhoto | UrlPlacePhoto;

export interface RegularHours {
  day: number; // 1=Monday … 7=Sunday
  open: string; // "HHMM"
  close: string; // "HHMM"
}

export interface PlaceHours {
  display?: string;
  isOpenNow?: boolean;
  regular?: Array<RegularHours>;
}

export type BusinessStatus = 'OPERATIONAL' | 'TEMPORARILY_CLOSED' | 'PERMANENTLY_CLOSED';

export interface AccessibilityOptions {
  wheelchairAccessibleParking?: boolean;
  wheelchairAccessibleEntrance?: boolean;
  wheelchairAccessibleRestroom?: boolean;
  wheelchairAccessibleSeating?: boolean;
}

export const appCategories = [
  'dining',
  'outdoors',
  'arts',
  'attractions',
  'nightlife',
  'sports',
  'shopping',
  'wellness',
  'lodging',
  'other',
] as const;

export type AppCategory = (typeof appCategories)[number];

export interface EstimatedDuration {
  minMinutes: number;
  maxMinutes: number;
}

export interface PlaceReview {
  rating: number;
  text?: string;
  authorName: string;
  authorPhotoUri?: string;
  relativeTimeDescription: string;
  publishTime: string;
}

export interface ParkingOptions {
  freeParkingLot?: boolean;
  paidParkingLot?: boolean;
  freeStreetParking?: boolean;
  paidStreetParking?: boolean;
  valetParking?: boolean;
  freeGarageParking?: boolean;
  paidGarageParking?: boolean;
}

export interface PaymentOptions {
  acceptsCreditCards?: boolean;
  acceptsDebitCards?: boolean;
  acceptsCashOnly?: boolean;
  acceptsNfc?: boolean;
}

export interface SocialMediaLinks {
  twitter?: string;
  instagram?: string;
  facebook?: string;
}

export interface RelatedPlaceSummary {
  id: string;
  name: string;
  categories: Array<PlaceCategory>;
}

export interface RelatedPlaces {
  parent?: RelatedPlaceSummary;
  children?: Array<RelatedPlaceSummary>;
  siblings?: Array<RelatedPlaceSummary>;
}

// FSQ "popular times" — same shape as PlaceHours.regular: 1=Mon…7=Sun, "HHMM".
export interface PopularPeriod {
  day: number;
  open: string;
  close: string;
}

export interface ItineraryItem {
  id: string;
  name: string;
  /** Where the item came from. `'user'` flags user-created custom pins
   *  (no photos / rating / hours / website etc.); UI components hide
   *  provider-only blocks when this is `'user'`. Omitted on legacy
   *  items in localStorage — treat absence as provider-sourced. */
  source?: 'foursquare' | 'user';
  latitude: number;
  longitude: number;
  address: string;
  categories: Array<PlaceCategory>;
  appCategory: AppCategory;
  hours?: PlaceHours;
  rating?: number; // 1–5 scale
  userRatingCount?: number;
  price?: number; // 0–4 (0=free, 4=very expensive)
  priceRange?: string; // formatted display string, e.g. "$10 – $50"
  website?: string;
  googleMapsUri?: string;
  phone?: string;
  photos: Array<PlacePhoto>;
  distanceMiles?: number;
  estimatedDuration: EstimatedDuration;
  suggestedGroupSize: string;
  businessStatus?: BusinessStatus;
  accessibilityOptions?: AccessibilityOptions;
  editorialSummary?: string;
  // Service options
  takeout?: boolean;
  delivery?: boolean;
  dineIn?: boolean;
  reservable?: boolean;
  curbsidePickup?: boolean;
  outdoorSeating?: boolean;
  // Dining attributes
  servesBreakfast?: boolean;
  servesBrunch?: boolean;
  servesLunch?: boolean;
  servesDinner?: boolean;
  servesBeer?: boolean;
  servesWine?: boolean;
  servesCocktails?: boolean;
  servesCoffee?: boolean;
  servesVegetarianFood?: boolean;
  servesDessert?: boolean;
  // Atmosphere
  goodForGroups?: boolean;
  goodForChildren?: boolean;
  allowsDogs?: boolean;
  liveMusic?: boolean;
  // Additional
  reviews?: Array<PlaceReview>;
  parkingOptions?: ParkingOptions;
  paymentOptions?: PaymentOptions;
  generativeSummary?: string;
  // Foursquare-only enrichments (left undefined for Google-sourced items)
  socialMedia?: SocialMediaLinks;
  menuUrl?: string;
  /** 0–1, server-side ranking signal */
  popularity?: number;
  /** FSQ taste tags (e.g. "trendy", "casual") */
  tastes?: Array<string>;
  /** Names of chains this place belongs to */
  chainNames?: Array<string>;
  relatedPlaces?: RelatedPlaces;
  /** Typical busy times of day, day-by-day */
  hoursPopular?: Array<PopularPeriod>;
}

/** Build a sparse, user-created ItineraryItem for a custom map pin.
 *  Every provider-only field (photos, rating, hours, website, …) is
 *  left undefined/empty so the UI's `source === 'user'` gates hide the
 *  matching panels. The `user:` id prefix makes it visually obvious in
 *  logs and guarantees no collision with provider place ids. */
export const buildUserPin = (input: {
  name: string;
  latitude: number;
  longitude: number;
  appCategory: AppCategory;
  address?: string;
}): ItineraryItem => ({
  id: `user:${crypto.randomUUID()}`,
  source: 'user',
  name: input.name.trim(),
  latitude: input.latitude,
  longitude: input.longitude,
  address: input.address?.trim() || '',
  categories: [],
  appCategory: input.appCategory,
  photos: [],
  // Default a 60-minute visit so timeline math has something to chew on.
  // The user can override per-place via the Curate-mode duration field.
  estimatedDuration: { minMinutes: 60, maxMinutes: 60 },
  suggestedGroupSize: '1-4',
});
